import { apiClient } from "./client";

// ---------- KGU params ----------

export interface KguParams {
  id: string;
  tenant_id: string;
  effective_from: string; // YYYY-MM-DD

  power_min_kw: number | string;
  power_max_kw: number | string;
  power_step_kw: number | string;

  self_needs_estimate_kw: number | string;
  switching_time_min: number;

  balance_tolerance_pct: number | string;
  heat_balance_tolerance_pct: number | string;
  direction_anomaly_threshold_kwh: number | string;

  imbalance_pos_discount_pct: number | string;
  imbalance_neg_penalty_pct: number | string;

  rdn_first_attempt_at: string; // HH:MM:SS
  rdn_retry_until: string;

  period_close_day: number;

  plan_submit_deadline: string;
  plan_reminder_at: string;

  has_bess: boolean;
}

export interface KguParamsWrite {
  effective_from: string;
  power_min_kw: number;
  power_max_kw: number;
  power_step_kw: number;
  self_needs_estimate_kw: number;
  switching_time_min: number;
  balance_tolerance_pct: number;
  heat_balance_tolerance_pct: number;
  direction_anomaly_threshold_kwh: number;
  imbalance_pos_discount_pct: number;
  imbalance_neg_penalty_pct: number;
  rdn_first_attempt_at: string;
  rdn_retry_until: string;
  period_close_day: number;
  plan_submit_deadline: string;
  plan_reminder_at: string;
  has_bess: boolean;
}

// ---------- Gas curve ----------

export interface GasCurvePoint {
  id: string;
  effective_from: string;
  power_kw: number | string;
  gas_m3h: number | string;
  source: "manual" | "calibrated_from_meter";
}

export interface GasCurveWrite {
  effective_from: string;
  points: { power_kw: number; gas_m3h: number }[];
}

// ---------- Cost parameters ----------

export interface CostParameter {
  id: string;
  tenant_id: string;
  effective_month: string; // YYYY-MM-DD (1st)
  code: string;
  name: string;
  value: number | string;
  unit: string;
  quantity: number | string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CostParameterWrite {
  code: string;
  name: string;
  value: number;
  unit: string;
  quantity: number;
  notes?: string | null;
}

export const parametersApi = {
  // KGU params
  getKguParams: (date?: string) =>
    apiClient
      .get<KguParams>("/kgu-params", { params: date ? { date } : undefined })
      .then((r) => r.data),
  putKguParams: (body: KguParamsWrite) =>
    apiClient.put<KguParams>("/kgu-params", body).then((r) => r.data),

  // Gas curve
  getGasCurve: (date?: string) =>
    apiClient
      .get<GasCurvePoint[]>("/kgu-gas-curve", { params: date ? { date } : undefined })
      .then((r) => r.data),
  putGasCurve: (body: GasCurveWrite) =>
    apiClient.put<GasCurvePoint[]>("/kgu-gas-curve", body).then((r) => r.data),

  // Cost parameters
  getCostParameters: (month: string) =>
    apiClient
      .get<CostParameter[]>("/cost-parameters", { params: { month } })
      .then((r) => r.data),
  putCostParameters: (month: string, items: CostParameterWrite[]) =>
    apiClient
      .put<CostParameter[]>("/cost-parameters", { items }, { params: { month } })
      .then((r) => r.data),
  copyFromPreviousMonth: (month: string) =>
    apiClient
      .post<CostParameter[]>(
        "/cost-parameters/copy-from-previous-month",
        null,
        { params: { month } },
      )
      .then((r) => r.data),
};
